import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import Tag from '../../../components/Backoffice/Display/Tag';
import { useDashboardContext } from '../../../contexts/DashboardContext';
import { CONSTANT, getConstant } from '../../../utils/constants';
import { formatDate, formatDateFromNowString, formatDateString } from '../../../utils/stringHelper';
import DashboardFilters from './DashboardFilters';
import DashboardSort from './DashboardSort';

function DashboardDatatable({ data, loading, onResidencySelect, datatableRef }) {
  const { dashboardContext, setDashboardOptions, setDashboardSort } = useDashboardContext();

  const onPage = (event) => {
    setDashboardOptions({ page: event.page, offset: event.first, rows: event?.rows });
  };

  const onSort = (event) => {
    setDashboardSort(
      CONSTANT.backoffice.residency.sortOptions.find(
        (c) => c.sortField === event.sortField && event.sortOrder === c.sortOrder
      )
    );
  };

  const nextAutomaticEmailTemplate = (rowData) => {
    if (!rowData.next_automatic_email_date) {
      return <span className="text-gray-400 italic text-sm">Seulement pour les séjours longs</span>;
    }
    const hasEmailSet = Boolean(rowData.email);
    return (
      <div className="flex flex-row gap-x-2 items-center">
        {formatDateFromNowString(rowData.next_automatic_email_date)}
        {!hasEmailSet && (
          <>
            <i
              data-pr-tooltip="Aucun email n'est renseigné"
              data-pr-position="left"
              className={`no-email-tooltip-${rowData.id} pi pi-exclamation-triangle text-custom-orange`}
            />
            <Tooltip target={`.no-email-tooltip-${rowData.id}`}></Tooltip>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex flex-row w-full justify-between">
        <DashboardFilters />
        <DashboardSort
          options={CONSTANT.backoffice.residency.sortOptions}
          value={dashboardContext.sortOption}
          onChange={onSort}
        />
      </div>
      <DataTable
        id={'dashboard-table-with-filters'}
        ref={datatableRef}
        exportFilename={`Liste Sejours Registre - ${formatDateString(new Date())}`}
        value={data.residencyList}
        lazy
        scrollable={true}
        loading={loading}
        onRowClick={(event) => onResidencySelect(event.data)}
        size="small"
        sortMode={'single'}
        paginator
        rows={dashboardContext.options?.rows}
        first={dashboardContext.options?.offset}
        filters={dashboardContext.options?.filters}
        sortField={dashboardContext.sortOption?.sortField}
        sortOrder={dashboardContext.sortOption?.sortOrder}
        totalRecords={data.totalRows}
        rowsPerPageOptions={[5, 20, 50, 100, 200, 500]}
        onPage={onPage}
        onSort={onSort}
        selectionMode="single">
        <Column
          frozen
          bodyClassName="overflow-hidden text-ellipsis whitespace-nowrap max-w-[350px] font-semibold"
          field="label"
          header="Nom"></Column>
        <Column
          bodyClassName="overflow-hidden text-ellipsis whitespace-nowrap max-w-[250px]"
          field="project_developer"
          header="Porteur.euse de projet"></Column>
        <Column
          field="type"
          header="Type"
          exportField={(rowData) =>
            getConstant('backoffice.residency.typeList', rowData.type)?.label
          }
          body={(rowData) => (
            <Tag tag={getConstant('backoffice.residency.typeList', rowData.type)} />
          )}></Column>
        <Column
          field="state"
          header="État"
          exportField={(rowData) =>
            getConstant('backoffice.residency.stateList', rowData.state)?.label
          }
          body={(rowData) => (
            <Tag tag={getConstant('backoffice.residency.stateList', rowData.state)} />
          )}></Column>
        <Column
          field="rooms"
          bodyClassName="overflow-hidden text-ellipsis whitespace-nowrap max-w-[350px]"
          header="Salles"
          exportField={(rowData) => rowData.rooms.map((r) => r.label).join(', ')}
          body={(rowData) => rowData.rooms.map((r) => r.label).join(', ')}></Column>
        <Column
          sortable
          body={(rowData) => formatDateString(rowData.start_dt)}
          exportField={(rowData) => formatDate(rowData.start_dt)}
          field={'start_dt'}
          header="Date de début"
          dataType="date"></Column>
        <Column
          body={(rowData) => formatDateString(rowData.end_dt)}
          exportField={(rowData) => formatDate(rowData.end_dt)}
          field={'end_dt'}
          header="Date de fin"
          dataType="date"></Column>
        <Column
          sortable
          body={(rowData) => formatDateFromNowString(rowData.custom_user?.last_signin_dt)}
          field={'custom_user.last_signin_dt'}
          sortField="custom_user__last_signin_dt"
          exportField={(rowData) => formatDate(rowData.custom_user?.last_signin_dt)}
          header="Dernière connexion"
          dataType="date"></Column>
        <Column
          sortable
          body={(rowData) => formatDateFromNowString(rowData.date_email_sent)}
          field={'date_email_sent'}
          header="Dernier mail essaimage"
          exportField={(rowData) => formatDate(rowData.date_email_sent)}
          dataType="date"></Column>
        <Column
          sortable
          body={(rowData) => nextAutomaticEmailTemplate(rowData)}
          field={'next_automatic_email_date'}
          header="Prochain mail essaimage"
          exportField={(rowData) => formatDate(rowData.next_automatic_email_date)}
          dataType="date"></Column>
        <Column
          field="properties"
          header="Tags"
          exportField={(rowData) => rowData.properties.map((r) => r.label).join(', ')}
          body={(rowData) => rowData.properties.map((r) => r.label).join(', ')}></Column>
      </DataTable>
    </div>
  );
}

export default DashboardDatatable;
